<template>
  <div class="page-container">
    <!-- 页头 -->
    <div class="page-header">
      <h3><i class="el-icon-s-data"></i> 喻点点 - 批量回传校验</h3>
    </div>

    <!-- 第一步：填写参数 -->
    <el-card class="section-card" shadow="hover">
      <div slot="header" class="card-header">
        <span class="step-badge">1</span> 填写参数
      </div>

      <el-form label-width="120px" label-position="left">
        <el-form-item label="Authorization">
          <el-input
            v-model="authorization"
            type="textarea"
            :rows="3"
            placeholder="请输入 JWT Token，如：eyJhbGciOiJIUzI1NiIs..."
          ></el-input>
        </el-form-item>

        <el-form-item label="Excel 文件">
          <el-upload
            ref="uploadRef"
            :show-file-list="true"
            :file-list="fileList"
            accept=".xlsx"
            :auto-upload="false"
            :limit="1"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
          >
            <el-button type="primary" icon="el-icon-folder-opened">选择文件</el-button>
            <span slot="tip" class="upload-tip">
              仅支持 .xlsx 格式，模板格式：账号名 | 账户ID | 数据源ID | h5链接
            </span>
          </el-upload>
        </el-form-item>

        <el-form-item>
          <el-button type="success" icon="el-icon-download" @click="downloadTemplate">下载模板</el-button>
          <el-button
            type="primary"
            icon="el-icon-upload2"
            :loading="submitting"
            :disabled="submitting || tableData.length === 0"
            @click="handleSubmit"
          >
            {{ submitting ? '提交中…' : '批量提交' }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 数据预览 / 执行结果 -->
    <el-card v-if="tableData.length > 0" class="section-card" shadow="hover">
      <div slot="header" class="card-header">
        <span class="step-badge">2</span> {{ submitted ? '执行结果' : '数据预览' }}
        <span class="header-count">共 {{ tableData.length }} 条</span>
      </div>

      <!-- 汇总统计卡片 -->
      <div v-if="submitted && summary" class="stats-row">
        <div class="stat-card stat-total">
          <div class="stat-num">{{ summary.total }}</div>
          <div class="stat-label">总计</div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-num">{{ summary.success }}</div>
          <div class="stat-label">成功</div>
        </div>
        <div class="stat-card stat-failed">
          <div class="stat-num">{{ summary.failed }}</div>
          <div class="stat-label">失败</div>
        </div>
      </div>

      <!-- 数据表格 -->
      <el-table
        :data="tableData"
        border
        stripe
        style="width:100%;"
        max-height="500"
        v-loading="submitting"
        :header-cell-style="{ background: '#f5f7fa', color: '#303133', fontWeight: 600 }"
        :row-class-name="tableRowClassName"
      >
        <el-table-column label="序号" width="65" align="center">
          <template slot-scope="scope">
            <span class="row-index">{{ scope.row.rowIndex }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="accountName" label="账号名" min-width="130">
          <template slot-scope="scope">
            <span class="cell-text">{{ scope.row.accountName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="accountId" label="账户ID" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            <code class="cell-code">{{ scope.row.accountId }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="dataSourceId" label="数据源ID" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">
            <code class="cell-code">{{ scope.row.dataSourceId }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="h5Link" label="h5链接" min-width="240" show-overflow-tooltip>
          <template slot-scope="scope">
            <a class="cell-link" :href="scope.row.h5Link" target="_blank" v-if="scope.row.h5Link">{{ scope.row.h5Link }}</a>
            <span v-else style="color:#c0c4cc;">-</span>
          </template>
        </el-table-column>
        <el-table-column label="结果" width="110" align="center">
          <template slot-scope="scope">
            <span v-if="!submitted" class="status-pending">待提交</span>
            <el-tag v-else-if="scope.row.success" type="success" size="small" effect="plain">成功</el-tag>
            <el-tag v-else type="danger" size="small" effect="plain">失败</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="详情" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="!submitted" style="color:#c0c4cc;">-</span>
            <span v-else-if="scope.row.success" class="detail-success">{{ scope.row.responseMsg || '-' }}</span>
            <el-tooltip v-else :content="scope.row.error" placement="top" :disabled="!scope.row.error">
              <span class="detail-error">{{ scope.row.error || '未知错误' }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import axios from "axios";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";

const BASE = "https://control.zjwec.cn";

export default {
  data: function () {
    return {
      authorization: "",
      selectedFile: null,
      fileList: [],
      submitting: false,
      submitted: false,
      tableData: [],
      summary: null
    };
  },
  methods: {
    // 文件选择 + 前端解析预览
    handleFileChange: function (file) {
      var that = this;
      that.selectedFile = file.raw;
      that.fileList = [file];
      that.submitted = false;
      that.summary = null;

      var reader = new FileReader();
      reader.onload = function (e) {
        try {
          var data = new Uint8Array(e.target.result);
          var wb = XLSX.read(data, { type: "array" });
          var sheetName = wb.SheetNames[0];
          var sheet = wb.Sheets[sheetName];
          var rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

          if (rows.length < 2) {
            that.$message.error("Excel 数据为空，至少需要一行表头和一行数据");
            that.tableData = [];
            return;
          }

          var parsed = [];
          for (var i = 1; i < rows.length; i++) {
            var row = rows[i];
            if (!row || !row.length) continue;

            var accountName = String(row[0] || "").trim();
            var accountId = String(row[1] || "").trim();
            var dataSourceId = String(row[2] || "").trim();
            var h5Link = String(row[3] || "").trim();

            if (!accountName && !accountId) continue;

            parsed.push({
              rowIndex: i + 1,
              accountName: accountName,
              accountId: accountId,
              dataSourceId: dataSourceId,
              h5Link: h5Link,
              success: false,
              responseMsg: "",
              error: ""
            });
          }

          if (parsed.length === 0) {
            that.$message.warning("未解析到有效数据，请检查 Excel 内容");
            that.tableData = [];
            return;
          }

          that.tableData = parsed;
          that.$message.success("成功解析 " + parsed.length + " 条数据");
        } catch (err) {
          console.error(err);
          that.$message.error("Excel 解析失败，请检查文件格式");
          that.tableData = [];
        }
      };
      reader.readAsArrayBuffer(file.raw);
    },

    // 文件移除
    handleFileRemove: function () {
      this.selectedFile = null;
      this.fileList = [];
      this.tableData = [];
      this.submitted = false;
      this.summary = null;
    },

    // 表格行样式
    tableRowClassName: function (obj) {
      if (!this.submitted) return "";
      return obj.row.success ? "row-success" : "row-failed";
    },

    // 下载模板
    downloadTemplate: function () {
      var headers = ["账号名", "账户ID", "数据源ID", "h5链接"];
      var exampleRow = [
        "yyrl291",
        "6f7bd428c54ea35997c0",
        "ds-202606038787",
        "https://land.ying-ting.com/index3.html?callback_id=xxx"
      ];
      var data = [headers, exampleRow];
      var ws = XLSX.utils.aoa_to_sheet(data);
      ws["!cols"] = [
        { wch: 14 },
        { wch: 26 },
        { wch: 20 },
        { wch: 60 }
      ];
      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "模板");
      var wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      FileSaver.saveAs(
        new Blob([wbout], { type: "application/octet-stream" }),
        "喻点点批量回传校验_模板.xlsx"
      );
      this.$message.success("模板下载成功");
    },

    // 提交
    handleSubmit: function () {
      var that = this;
      if (!that.authorization.trim()) {
        that.$message.warning("请填写 Authorization");
        return;
      }
      if (!that.selectedFile) {
        that.$message.warning("请选择 Excel 文件");
        return;
      }

      that.submitting = true;
      that.submitted = false;
      that.summary = null;

      var formData = new FormData();
      formData.append("authorization", that.authorization.trim());
      formData.append("file", that.selectedFile);

      axios
        .post(BASE + "/yudiandian/batchAddAccount", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then(function (resp) {
          var data = resp.data;
          that.summary = {
            total: data.total,
            success: data.success,
            failed: data.failed
          };

          var results = data.results || [];
          var resultMap = {};
          for (var i = 0; i < results.length; i++) {
            resultMap[results[i].row] = results[i];
          }

          for (var j = 0; j < that.tableData.length; j++) {
            var row = that.tableData[j];
            var match = resultMap[row.rowIndex];
            if (match) {
              that.$set(row, "success", match.success);
              if (match.success) {
                that.$set(row, "responseMsg", match.response ? match.response.msg : "");
                that.$set(row, "error", "");
              } else {
                that.$set(row, "responseMsg", "");
                that.$set(row, "error", match.error || "未知错误");
              }
            } else {
              that.$set(row, "success", false);
              that.$set(row, "responseMsg", "");
              that.$set(row, "error", "未匹配到返回结果");
            }
          }

          that.submitted = true;

          var failCount = data.failed || 0;
          if (failCount === 0) {
            that.$message.success("全部 " + data.total + " 条执行成功");
          } else {
            that.$message.warning("成功 " + data.success + " 条，失败 " + failCount + " 条");
          }
        })
        .catch(function (err) {
          that.$message.error("请求失败：" + (err.message || "网络异常"));
        })
        .finally(function () {
          that.submitting = false;
        });
    }
  }
};
</script>

<style scoped>
.page-container {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e4e7ed;
}

.page-header h3 {
  margin: 0;
  font-size: 20px;
  color: #303133;
}

.page-header h3 i {
  color: #409eff;
  margin-right: 8px;
}

.section-card {
  margin-bottom: 20px;
  border-radius: 8px;
}

.section-card :deep(.el-card__header) {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%);
  padding: 14px 20px;
  border-bottom: 1px solid #e4e7ed;
}

.card-header {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
}

.step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  font-size: 13px;
  font-weight: bold;
  margin-right: 10px;
  flex-shrink: 0;
}

.header-count {
  margin-left: auto;
  font-size: 13px;
  color: #909399;
  font-weight: normal;
}

.upload-tip {
  margin-left: 16px;
  font-size: 12px;
  color: #909399;
  vertical-align: middle;
}

/* 统计卡片 */
.stats-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.stat-card {
  flex: 1;
  min-width: 140px;
  border-radius: 8px;
  padding: 20px 24px;
  text-align: center;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-total {
  background: #f0f5ff;
  border: 1px solid #d6e4ff;
}

.stat-success {
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
}

.stat-failed {
  background: #fef0f0;
  border: 1px solid #fde2e2;
}

.stat-num {
  font-size: 32px;
  font-weight: bold;
  font-family: 'Courier New', monospace;
  line-height: 1.2;
}

.stat-total .stat-num {
  color: #409eff;
}

.stat-success .stat-num {
  color: #67c23a;
}

.stat-failed .stat-num {
  color: #f56c6c;
}

.stat-label {
  font-size: 13px;
  color: #5e6b82;
  margin-top: 6px;
}

/* 表格样式 */
.row-index {
  color: #909399;
  font-size: 12px;
}

.cell-text {
  font-weight: 500;
  color: #303133;
}

.cell-code {
  font-size: 12px;
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  color: #606266;
}

.cell-link {
  color: #409eff;
  text-decoration: none;
  font-size: 12px;
}

.cell-link:hover {
  text-decoration: underline;
}

.status-pending {
  color: #c0c4cc;
  font-size: 12px;
}

.detail-success {
  color: #67c23a;
  font-size: 13px;
}

.detail-error {
  color: #f56c6c;
  cursor: pointer;
  font-size: 13px;
}

/* 行高亮 */
.el-table :deep(.row-success) {
  background-color: #f0f9eb !important;
}

.el-table :deep(.row-failed) {
  background-color: #fef0f0 !important;
}

/* 提交按钮间距 */
.el-form-item .el-button + .el-button {
  margin-left: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/yudiandian/batchAddAccount.vue